import React, { useEffect, useRef, useState } from "react";

// Canvas d'animation premium, sobre B2B (fond blanc), sans particules
// Phases: enter -> scan (boucle) -> check (sur signal externe)
// Animation 3D vectorielle: rotation perspective, rebond, flottement

function CineUploadCanvas({ width = 420, height = 300, readyToComplete = false, onExitComplete }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const stateRef = useRef(null);
  // Ref pour que la boucle RAF lise toujours la valeur courante
  const readyRef = useRef(readyToComplete);
  useEffect(() => { readyRef.current = readyToComplete; }, [readyToComplete]);
  const onExitCompleteRef = useRef(onExitComplete);
  useEffect(() => { onExitCompleteRef.current = onExitComplete; }, [onExitComplete]);
  const exitFiredRef = useRef(false);
  useEffect(() => { if (!readyToComplete) exitFiredRef.current = false; }, [readyToComplete]);

  // Wait 3 seconds after readyToComplete before triggering exit
  const [delayedReady, setDelayedReady] = useState(false);
  useEffect(() => {
    if (!readyToComplete) {
      setDelayedReady(false);
      return;
    }
    const timer = setTimeout(() => setDelayedReady(true), 1500);
    return () => clearTimeout(timer);
  }, [readyToComplete]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const ctx = c.getContext("2d");

    const W = width;
    const H = height;

    c.width = Math.floor(W * dpr);
    c.height = Math.floor(H * dpr);
    c.style.width = W + "px";
    c.style.height = H + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    stateRef.current = {
      t: 0,
      phase: "enter",
      // Position Y + animation 3D
      docY: H + 140,
      targetY: Math.max(24, H * 0.12),
      // Rotation 3D simulée (angle en radians, -0.5 = tourné vers la droite)
      rotY: -0.6,
      targetRotY: 0,
      // Scale (effet de profondeur)
      scale: 0.7,
      targetScale: 1,
      // Rebond
      bouncePhase: 0,
      // Position X (légère dérive)
      offsetX: 40,
      targetOffsetX: 0,
      // Flottement pendant scan
      floatT: 0,
      scanY: 0,
      scanAlpha: 0,
      checkAlpha: 0,
      // Phase EXIT: disparition élégante
      exitT: 0,
      docAlpha: 1,
      checkHoldFrames: 0,
      lines: [
        { w: 0.82, d: 28, sig: false },
        { w: 0.64, d: 36, sig: false },
        { w: 0.86, d: 44, sig: false },
        { w: 0.58, d: 52, sig: false },
        { w: 0.52, d: 60, sig: true },
        { w: 0.74, d: 68, sig: false },
        { w: 0.44, d: 76, sig: true },
        { w: 0.70, d: 84, sig: false },
      ],
    };

    const lerp = (a, b, t) => a + (b - a) * t;
    const easeOutBack = (t) => {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    };

    function roundRect(ctx, x, y, w, h, r) {
      ctx.beginPath(); ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y); ctx.closePath();
    }

    // Dessine le document avec effet 3D (rotation Y simulée par skew + scale)
    function drawDoc3D(cx, cy, w, h, s, rotY, scale, alpha) {
      const primary = getComputedStyle(document.documentElement).getPropertyValue('--color-primary') || '#0B3D91';
      if (alpha <= 0) return;

      ctx.save();
      ctx.globalAlpha = alpha;

      // Centre de transformation
      ctx.translate(cx, cy);
      
      // Simulation rotation Y: compression horizontale + skew
      const scaleX = Math.cos(rotY) * scale;
      const skewY = Math.sin(rotY) * 0.15;
      ctx.transform(scaleX, skewY, 0, scale, 0, 0);

      const x = -w / 2;
      const y = -h / 2;

      // Ombre dynamique (décalée selon rotation)
      const shadowOffsetX = rotY * 25;
      const shadowOffsetY = 12 + (1 - scale) * 20;
      ctx.shadowColor = 'rgba(0,0,0,0.12)';
      ctx.shadowBlur = 24 + (1 - scale) * 15;
      ctx.shadowOffsetX = shadowOffsetX;
      ctx.shadowOffsetY = shadowOffsetY;

      // Carte principale
      ctx.fillStyle = 'rgba(255,255,255,0.99)';
      roundRect(ctx, x, y, w, h, 12);
      ctx.fill();

      // Reset shadow pour le reste
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Bordure subtile
      ctx.strokeStyle = 'rgba(0,0,0,0.08)';
      ctx.lineWidth = 1.5;
      roundRect(ctx, x, y, w, h, 12);
      ctx.stroke();

      // Coin plié (effet 3D)
      const foldSize = 18;
      ctx.fillStyle = '#E8EEF6';
      ctx.beginPath();
      ctx.moveTo(x + w - foldSize, y);
      ctx.lineTo(x + w, y + foldSize);
      ctx.lineTo(x + w, y);
      ctx.closePath();
      ctx.fill();
      // Ligne de pli
      ctx.strokeStyle = 'rgba(0,0,0,0.06)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x + w - foldSize, y);
      ctx.lineTo(x + w, y + foldSize);
      ctx.stroke();

      // Barre titre
      ctx.fillStyle = '#D4E0F0';
      roundRect(ctx, x + 14, y + 18, w * 0.55, 6, 3);
      ctx.fill();

      // Lignes de texte avec animation progressive
      s.lines.forEach((ln, i) => {
        const prog = Math.max(0, Math.min(1, (s.t - ln.d) / 18));
        if (prog <= 0) return;
        const lx = x + 14;
        const ly = y + 38 + i * 16;
        const lw = (w - 28) * ln.w * prog;
        const lh = 4;
        ctx.fillStyle = ln.sig ? primary.trim() : '#E2EAF2';
        roundRect(ctx, lx, ly, lw, lh, 2);
        ctx.fill();
      });

      ctx.restore();
    }

    function frame() {
      const s = stateRef.current;
      if (!s) return;
      s.t++;
      ctx.clearRect(0, 0, W, H);

      const dw = Math.min(165, Math.max(125, W * 0.40));
      const dh = Math.min(215, Math.max(165, H * 0.64));
      const baseCx = W / 2;

      // Phase ENTER: animation 3D d'arrivée
      if (s.phase === 'enter') {
        // Interpolation fluide avec easing
        const speed = 0.09;
        s.docY = lerp(s.docY, s.targetY, speed);
        s.rotY = lerp(s.rotY, s.targetRotY, speed * 1.2);
        s.scale = lerp(s.scale, s.targetScale, speed);
        s.offsetX = lerp(s.offsetX, s.targetOffsetX, speed * 0.9);

        // Vérifier si l'animation d'entrée est terminée
        const arrived = Math.abs(s.docY - s.targetY) < 0.8 &&
                        Math.abs(s.rotY - s.targetRotY) < 0.02 &&
                        Math.abs(s.scale - s.targetScale) < 0.01;

        if (arrived) {
          s.docY = s.targetY;
          s.rotY = s.targetRotY;
          s.scale = s.targetScale;
          s.offsetX = s.targetOffsetX;
          s.phase = 'scan';
        }
      }

      // Phase SCAN: léger flottement 3D
      if (s.phase === 'scan' || s.phase === 'check') {
        s.floatT += 0.04;
      }

      // Phase EXIT: animation de sortie élégante
      if (s.phase === 'exit') {
        s.exitT += 0.018;
        s.floatT += 0.04;
        
        const exitEase = 1 - Math.pow(1 - Math.min(s.exitT, 1), 2);
        s.docY = lerp(s.targetY, -100, exitEase);
        s.rotY = lerp(0, 0.5, exitEase);
        s.scale = lerp(1, 0.5, exitEase);
        s.docAlpha = lerp(1, 0, exitEase);
        s.offsetX = lerp(0, -40, exitEase);

        // Quand tout est invisible → signaler la fin
        if (s.docAlpha < 0.02 && s.checkAlpha < 0.02 && !exitFiredRef.current) {
          exitFiredRef.current = true;
          onExitCompleteRef.current?.();
        }
      }

      // Calcul position finale avec flottement
      const floatY = (s.phase === 'scan' || s.phase === 'check' || s.phase === 'exit') 
        ? Math.sin(s.floatT) * 2.5 
        : 0;
      const floatRot = (s.phase === 'scan' || s.phase === 'check' || s.phase === 'exit')
        ? Math.sin(s.floatT * 0.7) * 0.015
        : 0;

      const cx = baseCx + s.offsetX;
      const cy = s.docY + dh / 2 + floatY;

      // Dessiner le document 3D (avec alpha pour la phase exit)
      if (s.docAlpha > 0.01) {
        drawDoc3D(cx, cy, dw, dh, s, s.rotY + floatRot, s.scale, s.docAlpha);
      }

      // Ligne de scan
      if (s.phase === 'scan') {
        s.scanAlpha = Math.min(s.scanAlpha + 0.08, 1);
        s.scanY += 2.2;
        if (s.scanY > dh) s.scanY = 0;

        const scanWorldY = s.docY + s.scanY + floatY;
        const primary = getComputedStyle(document.documentElement).getPropertyValue('--color-primary') || '#0B3D91';
        
        // Scan bar plus visible
        const grad = ctx.createLinearGradient(cx - dw/2, scanWorldY - 10, cx - dw/2, scanWorldY + 10);
        grad.addColorStop(0, 'rgba(11,61,145,0)');
        grad.addColorStop(0.5, `rgba(11,61,145,${0.45 * s.scanAlpha})`);
        grad.addColorStop(1, 'rgba(11,61,145,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(cx - dw/2 * s.scale, scanWorldY - 10, dw * s.scale, 20);

        // Après 3 secondes et quand ready → check
        if (delayedReady && readyRef.current) {
          s.phase = 'check';
        }
      }

      // Checkmark
      if (s.phase === 'check' || s.phase === 'exit') {
        // Phase check: le check apparaît
        if (s.phase === 'check') {
          s.checkAlpha = Math.min(s.checkAlpha + 0.045, 1);
          
          // Une fois le check fully visible, on attend un peu puis on passe en exit
          if (s.checkAlpha >= 1) {
            s.checkHoldFrames++;
            if (s.checkHoldFrames > 25) { // ~0.4s de pause avec le check visible (réduit de 0.8s)
              s.phase = 'exit';
            }
          }
        }
        
        // Phase exit: le check fade out progressivement
        if (s.phase === 'exit') {
          s.checkAlpha = Math.max(0, s.checkAlpha - 0.025);
        }
        
        const a = s.checkAlpha;
        if (a > 0.01) {
          // Badge de validation sur le coin du document (plus premium que centré)
          const checkCx = cx + dw * 0.28;
          const checkCy = cy - dh * 0.30;
          const radius = 18;

          // Cercle de fond
          ctx.beginPath();
          ctx.arc(checkCx, checkCy, radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(82,196,26,${a * 0.15})`;
          ctx.fill();

          // Cercle bordure
          ctx.beginPath();
          ctx.arc(checkCx, checkCy, radius, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(82,196,26,${a * 0.85})`;
          ctx.lineWidth = 1.8;
          ctx.stroke();

          // Check mark avec animation de dessin
          ctx.save();
          ctx.globalAlpha = a;
          ctx.beginPath();
          ctx.moveTo(checkCx - 7, checkCy + 1);
          ctx.lineTo(checkCx - 2, checkCy + 6);
          ctx.lineTo(checkCx + 8, checkCy - 6);
          ctx.strokeStyle = '#52C41A';
          ctx.lineWidth = 2.2;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
          ctx.restore();
        }
      }

      rafRef.current = requestAnimationFrame(frame);
    }

    rafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafRef.current);
  }, [width, height, delayedReady]);

  return (
    <canvas ref={canvasRef} style={{ display: 'block', width, height }} aria-hidden="true" />
  );
}

export default CineUploadCanvas;

